import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Instagram, Facebook, Music2, Mail, MessageCircle, Radio, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const PERIODS = [["today", "Today"], ["7d", "Last 7 Days"], ["30d", "Last 30 Days"], ["all", "All Time"]];

const CARDS = [
  ["instagram", "Instagram Clicks", Instagram],
  ["facebook", "Facebook Clicks", Facebook],
  ["tiktok", "TikTok Clicks", Music2],
  ["whatsapp_chat", "WhatsApp Chat Clicks", MessageCircle],
  ["whatsapp_channel", "WhatsApp Channel Clicks", Radio],
  ["whatsapp_community", "WhatsApp Community Clicks", Users],
  ["email", "Email Clicks", Mail],
];

export function EngagementTab() {
  const [period, setPeriod] = useState("7d");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/admin/engagement?period=${period}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [period]);

  return (
    <div className="mt-6" data-testid="admin-engagement-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="font-display text-2xl uppercase">Social Engagement</p>
          <p className="text-xs text-ink/50 mt-1">
            Outbound <strong>clicks</strong> from the website. A click does not mean someone followed or joined — these are click counts only.
          </p>
        </div>
        <div className="flex gap-2">
          {PERIODS.map(([k, l]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`engagement-period-${k}`}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-colors ${period === k ? "bg-ink text-cream border-ink" : "bg-white border-ink/15 hover:border-ink"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {!data ? <p className="text-ink/50 font-semibold" data-testid="engagement-loading">Loading...</p> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {CARDS.map(([key, label, Icon]) => (
              <div key={key} className="bg-white border border-black/10 p-4" data-testid={`engagement-${key}`}>
                <Icon className="w-4 h-4 text-flame mb-2" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-ink/50 leading-tight">{label}</p>
                <p className="font-display text-3xl mt-1.5">{data.by_action[key] ?? 0}</p>
              </div>
            ))}
          </div>

          <p className="text-xs font-bold uppercase tracking-widest text-ink/50 mt-8 mb-3">Clicks by page</p>
          {data.by_page.length === 0 ? (
            <div className="bg-white border border-dashed border-ink/20 p-10 text-center" data-testid="engagement-empty">
              <p className="font-display text-xl uppercase text-ink/40">No social engagement data yet.</p>
              <p className="text-xs text-ink/40 mt-2">Clicks on the footer icons, Connect section and WhatsApp menu will appear here.</p>
            </div>
          ) : (
            <div className="bg-white border border-black/10 divide-y divide-black/5" data-testid="engagement-pages">
              {data.by_page.map((p) => (
                <div key={p.page} className="flex items-center justify-between px-4 py-3">
                  <p className="text-sm font-semibold font-mono">{p.page}</p>
                  <p className="text-sm font-display text-lg">{p.clicks} <span className="text-[10px] font-sans font-bold uppercase tracking-widest text-ink/40">clicks</span></p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
